import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Music, 
    Calendar, 
    FileAudio, 
    ExternalLink, 
    Search,
    Loader2,
    Building2,
    Users,
    DollarSign,
    Trash2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function OpportunitiesPage() {
    const { token, isAdmin } = useAuth();
    const [commercialOpps, setCommercialOpps] = useState([]);
    const [postedProjects, setPostedProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sourceTab, setSourceTab] = useState('commercial');
    const [statusTab, setStatusTab] = useState('open');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, opportunity: null, isPosted: false });
    const [deleting, setDeleting] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        if (sourceTab === 'commercial') {
            fetchCommercialOpportunities();
        } else {
            fetchPostedProjects();
        }
    }, [sourceTab, statusTab]);

    const fetchCommercialOpportunities = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API}/opportunities?status=${statusTab}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCommercialOpps(data);
            }
        } catch (error) {
            toast.error('Error loading opportunities');
        } finally {
            setLoading(false);
        }
    };

    const fetchPostedProjects = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API}/posted-projects?status=${statusTab}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPostedProjects(data);
            }
        } catch (error) {
            toast.error('Error loading projects');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOpportunity = async () => {
        if (!deleteDialog.opportunity) return;
        
        setDeleting(true);
        try {
            // Determine the correct endpoint based on type
            const isPostedProject = deleteDialog.isPosted;
            const id = isPostedProject ? deleteDialog.opportunity.id : deleteDialog.opportunity.sync_id;
            const endpoint = isPostedProject 
                ? `${API}/poster/projects/${id}`
                : `${API}/opportunities/${id}`;
            
            const response = await fetch(endpoint, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                toast.success(isPostedProject ? 'Project deleted successfully' : 'Opportunity deleted successfully');
                if (data.had_submissions) {
                    toast.info(`Note: ${data.submission_count} submission(s) were associated with this ${isPostedProject ? 'project' : 'opportunity'}`);
                }
                // Refresh the appropriate list
                if (isPostedProject) {
                    fetchPostedProjects();
                } else {
                    fetchCommercialOpportunities();
                }
            } else {
                const error = await response.json();
                toast.error(error.detail || `Failed to delete ${isPostedProject ? 'project' : 'opportunity'}`);
            }
        } catch (error) {
            toast.error('Error deleting');
        } finally {
            setDeleting(false);
            setDeleteDialog({ open: false, opportunity: null, isPosted: false });
        }
    };

    const currentOpportunities = sourceTab === 'commercial' ? commercialOpps : postedProjects;

    const filteredOpportunities = currentOpportunities.filter(opp => {
        const title = opp.title?.toLowerCase() || '';
        const needed = opp.needed?.toLowerCase() || '';
        const description = opp.description?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return title.includes(search) || needed.includes(search) || description.includes(search);
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    return (
        <Layout>
            <div className="max-w-7xl mx-auto" data-testid="opportunities-page">
                {/* Header */}
                <div className="mb-8">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-3xl lg:text-4xl font-bold text-white mb-2"
                    >
                        Sync Opportunities
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-[#A3A3A3]"
                    >
                        Browse and submit to available sync licensing opportunities
                    </motion.p>
                </div>

                {/* Source Tabs (Commercial vs Posted) */}
                <Tabs value={sourceTab} onValueChange={setSourceTab} className="mb-6">
                    <TabsList className="bg-[#1A1A1A] border border-[#333] p-1 mb-6">
                        <TabsTrigger 
                            value="commercial"
                            data-testid="tab-commercial"
                            className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white px-6"
                        >
                            <Building2 className="w-4 h-4 mr-2" />
                            Commercial
                        </TabsTrigger>
                        <TabsTrigger 
                            value="posted"
                            data-testid="tab-posted"
                            className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white px-6"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Posted Projects
                        </TabsTrigger>
                    </TabsList>

                    {/* Status Tabs and Search */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
                        <Tabs value={statusTab} onValueChange={setStatusTab}>
                            <TabsList className="bg-[#262626] border border-[#404040]">
                                <TabsTrigger 
                                    value="open"
                                    data-testid="tab-open"
                                    className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white"
                                >
                                    Open
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="closed"
                                    data-testid="tab-closed"
                                    className="data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white"
                                >
                                    Closed
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                            <Input
                                placeholder="Search opportunities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                data-testid="search-input"
                                className="pl-10 h-10 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500"
                            />
                        </div>
                    </div>

                    {/* Commercial Tab Content */}
                    <TabsContent value="commercial" className="mt-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                            </div>
                        ) : filteredOpportunities.length === 0 ? (
                            <EmptyState 
                                message="No commercial opportunities found" 
                                description="Commercial opportunities are sourced from industry sync platforms"
                            />
                        ) : (
                            <OpportunityGrid 
                                opportunities={filteredOpportunities} 
                                containerVariants={containerVariants}
                                itemVariants={itemVariants}
                                isPosted={false}
                                isAdmin={isAdmin}
                                onDelete={(opp) => setDeleteDialog({ open: true, opportunity: opp })}
                            />
                        )}
                    </TabsContent>

                    {/* Posted Projects Tab Content */}
                    <TabsContent value="posted" className="mt-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                            </div>
                        ) : filteredOpportunities.length === 0 ? (
                            <EmptyState 
                                message="No posted projects found" 
                                description="Posted projects come from verified project owners looking for music"
                            />
                        ) : (
                            <OpportunityGrid 
                                opportunities={filteredOpportunities} 
                                containerVariants={containerVariants}
                                itemVariants={itemVariants}
                                isPosted={true}
                                isAdmin={isAdmin}
                                onDelete={(opp) => setDeleteDialog({ open: true, opportunity: opp, isPosted: true })}
                            />
                        )}
                    </TabsContent>
                </Tabs>

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, opportunity: null, isPosted: false })}>
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">
                                Delete {deleteDialog.isPosted ? 'Project' : 'Opportunity'}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-[#A3A3A3]">
                                Are you sure you want to delete "<span className="text-white">{deleteDialog.opportunity?.title}</span>"?
                                This action cannot be undone. Any existing submissions will remain in the system but will no longer be linked to this {deleteDialog.isPosted ? 'project' : 'opportunity'}.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#262626] border-[#404040] text-white hover:bg-[#333]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeleteOpportunity}
                                disabled={deleting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Layout>
    );
}

function OpportunityGrid({ opportunities, containerVariants, itemVariants, isPosted, isAdmin, onDelete }) {
    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        >
            {opportunities.map((opp) => (
                <motion.div key={opp.sync_id || opp.id} variants={itemVariants}>
                    {isPosted ? (
                        <PostedProjectCard project={opp} isAdmin={isAdmin} onDelete={onDelete} />
                    ) : (
                        <OpportunityCard opportunity={opp} isAdmin={isAdmin} onDelete={onDelete} />
                    )}
                </motion.div>
            ))}
        </motion.div>
    );
}

function OpportunityCard({ opportunity, isAdmin, onDelete }) {
    const referenceCount = opportunity.reference_links?.length || 0;

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) onDelete(opportunity);
    };

    return (
        <Link 
            to={`/opportunities/${opportunity.sync_id}`}
            data-testid={`opp-card-${opportunity.sync_id}`}
            className="block"
        >
            <div className="card-interactive p-6 h-full flex flex-col">
                {/* Status badge */}
                <div className="flex items-center justify-between mb-4">
                    <Badge 
                        variant="outline"
                        className={opportunity.status === 'open' ? 'status-open' : 'status-closed'}
                    >
                        {opportunity.status}
                    </Badge>
                    <div className="flex items-center gap-2">
                        {referenceCount > 0 && (
                            <div className="flex items-center gap-1 text-xs text-[#A3A3A3]">
                                <ExternalLink size={14} />
                                <span>{referenceCount} refs</span>
                            </div>
                        )}
                        {isAdmin && onDelete && (
                            <button
                                onClick={handleDelete}
                                data-testid={`delete-opp-${opportunity.sync_id}`}
                                className="p-1.5 rounded-lg text-[#666] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                title="Delete opportunity"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-white mb-3 line-clamp-2">
                    {opportunity.title}
                </h3>

                {/* Needed */}
                {opportunity.needed && (
                    <p className="text-sm text-[#A3A3A3] mb-4 line-clamp-2 flex-1">
                        {opportunity.needed}
                    </p>
                )}

                {/* Meta info */}
                <div className="flex flex-wrap gap-3 mt-auto pt-4 border-t border-[#404040]">
                    {opportunity.deadline_text && (
                        <div className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                            <Calendar size={14} />
                            <span>{opportunity.deadline_text}</span>
                        </div>
                    )}
                    {opportunity.accepted_file_types?.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                            <FileAudio size={14} />
                            <span>{opportunity.accepted_file_types.join(', ')}</span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

function PostedProjectCard({ project, isAdmin, onDelete }) {
    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) onDelete(project);
    };

    return (
        <Link 
            to={`/posted-projects/${project.id}`}
            data-testid={`project-card-${project.id}`}
            className="block"
        >
            <div className="card-interactive p-6 h-full flex flex-col">
                {/* Status and poster info */}
                <div className="flex items-center justify-between mb-4">
                    <Badge 
                        variant="outline"
                        className={project.status === 'open' ? 'status-open' : 'status-closed'}
                    >
                        {project.status}
                    </Badge>
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-[#A3A3A3]">
                            by {project.poster_company || project.poster_name}
                        </div>
                        {isAdmin && onDelete && (
                            <button
                                onClick={handleDelete}
                                data-testid={`delete-project-${project.id}`}
                                className="p-1.5 rounded-lg text-[#666] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                title="Delete project"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-white mb-3 line-clamp-2">
                    {project.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-[#A3A3A3] mb-4 line-clamp-2 flex-1">
                    {project.description}
                </p>

                {/* Genres */}
                <div className="flex flex-wrap gap-1 mb-4">
                    {project.genres?.slice(0, 3).map((genre, idx) => (
                        <span 
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-[#262626] rounded-full text-[#888]"
                        >
                            {genre}
                        </span>
                    ))}
                    {project.genres?.length > 3 && (
                        <span className="text-xs px-2 py-0.5 bg-[#262626] rounded-full text-[#888]">
                            +{project.genres.length - 3}
                        </span>
                    )}
                </div>

                {/* Meta info */}
                <div className="flex flex-wrap gap-3 mt-auto pt-4 border-t border-[#404040]">
                    <div className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                        <Calendar size={14} />
                        <span>Due {formatDate(project.deadline)}</span>
                    </div>
                    {project.budget && (
                        <div className="flex items-center gap-1.5 text-xs text-green-400">
                            <DollarSign size={14} />
                            <span>${project.budget.toLocaleString()}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-[#A3A3A3]">
                        <Users size={14} />
                        <span>{project.submission_count || 0} submissions</span>
                    </div>
                </div>
            </div>
        </Link>
    );
}

function EmptyState({ message, description }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mb-4">
                <Music size={32} className="text-[#A3A3A3]" />
            </div>
            <p className="text-lg text-[#A3A3A3] mb-2">{message}</p>
            {description && <p className="text-sm text-[#666]">{description}</p>}
        </div>
    );
}
